import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, PanInfo } from "framer-motion";
import {
  Check,
  X,
  HelpCircle,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  Users,
  Eye,
} from "lucide-react";
import { votingDisplayNameService } from "@core/services/votingDisplayNameService";

interface PollOption {
  id: string;
  poll_id: string;
  option_date: string;
  time_slots: Array<{
    hour: number;
    minute: number;
    duration?: number;
  }>;
  display_order: number;
}

interface Vote {
  id: string;
  poll_id: string;
  respondent_name?: string;
  response_data: Record<string, "yes" | "no" | "maybe">;
  created_at: string;
}

interface VoteGridProps {
  options: PollOption[];
  votes: Vote[];
  currentVote: Record<string, "yes" | "no" | "maybe">;
  userHasVoted: Record<string, boolean>;
  onVoteChange: (optionId: string, value: "yes" | "no" | "maybe") => void;
  onHaptic: (type: "light" | "medium" | "heavy") => void;
}

const VoteButton: React.FC<{
  type: "yes" | "no" | "maybe";
  isSelected: boolean;
  isColored: boolean;
  onSelect: () => void;
  onHaptic: (type: "light" | "medium" | "heavy") => void;
}> = ({ type, isSelected, isColored, onSelect, onHaptic }) => {
  const { t } = useTranslation("common");
  const config = {
    yes: {
      icon: Check,
      bgColor: "bg-blue-500",
      bgColorBright: "bg-blue-600",
      hoverColor: "hover:bg-blue-100",
      textColor: "text-blue-600",
      borderColor: "border-blue-500",
      label: t("vote_grid.yes"),
    },
    no: {
      icon: X,
      bgColor: "bg-red-500",
      bgColorBright: "bg-red-600",
      hoverColor: "hover:bg-red-100",
      textColor: "text-red-600",
      borderColor: "border-red-500",
      label: t("vote_grid.no"),
    },
    maybe: {
      icon: HelpCircle,
      bgColor: "bg-orange-500",
      bgColorBright: "bg-orange-600",
      hoverColor: "hover:bg-orange-100",
      textColor: "text-orange-600",
      borderColor: "border-orange-500",
      label: t("vote_grid.maybe"),
    },
  };

  const {
    icon: Icon,
    bgColor,
    bgColorBright,
    hoverColor,
    textColor,
    borderColor,
    label,
  } = config[type];

  const handleSelect = () => {
    onHaptic("medium");
    onSelect();
  };

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.05 }}
      onClick={handleSelect}
      data-testid={`vote-button-${type}`}
      aria-label={label}
      className={`
        flex-1 min-h-[44px] min-w-[80px] rounded-xl flex items-center justify-center gap-1.5 font-medium text-sm
        transition-all duration-200 border-2 active:scale-95
        ${
          isColored
            ? `bg-white ${borderColor} shadow-lg scale-105`
            : `bg-white ${textColor} ${hoverColor} border-gray-200`
        }
      `}
    >
      {type === "maybe" ? (
        <span className={`${textColor} text-2xl font-bold`}>?</span>
      ) : (
        <Icon className={`h-6 w-6 ${textColor}`} />
      )}
    </motion.button>
  );
};

const ProgressBar: React.FC<{
  type: "yes" | "no" | "maybe";
  existingCount: number;
  totalExisting: number;
  userVote?: "yes" | "no" | "maybe";
  userHasVoted: boolean;
}> = ({ type, existingCount, totalExisting, userVote, userHasVoted }) => {
  const config = {
    yes: { color: "bg-blue-500", colorLight: "bg-blue-200" },
    no: { color: "bg-red-500", colorLight: "bg-red-200" },
    maybe: { color: "bg-orange-500", colorLight: "bg-orange-200" },
  };

  const { color, colorLight } = config[type];

  // Calcul des pourcentages
  const existingPercentage =
    totalExisting > 0 ? (existingCount / totalExisting) * 100 : 0;
  const withUserPercentage =
    totalExisting > 0
      ? ((existingCount + (userVote === type && userHasVoted ? 1 : 0)) /
          (totalExisting + (userHasVoted ? 1 : 0))) *
        100
      : 0;

  // Condition pour afficher la barre de surbrillance
  const showUserEffect = userVote === type && userHasVoted;

  return (
    <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
      {/* Barre de fond (vraies statistiques, 20% opacité) */}
      {existingPercentage > 0 && (
        <div
          className={`absolute top-0 left-0 h-full ${colorLight} opacity-30`}
          style={{ width: `${existingPercentage}%` }}
        />
      )}

      {/* Barre de surbrillance - SEULEMENT l'ajout du vote utilisateur */}
      {showUserEffect && (
        <div
          className={`absolute top-0 h-full ${color} opacity-75`}
          style={{
            left: `${existingPercentage}%`,
            width: `${Math.max(0, withUserPercentage - existingPercentage)}%`,
          }}
        />
      )}
    </div>
  );
};

const OptionCard: React.FC<{
  option: PollOption;
  currentVote?: "yes" | "no" | "maybe";
  userHasVoted: boolean;
  voteCounts: { yes: number; no: number; maybe: number };
  voterNames: string[];
  onVoteChange: (value: "yes" | "no" | "maybe") => void;
  onHaptic: (type: "light" | "medium" | "heavy") => void;
  rank?: number;
  showDate?: boolean;
  isTopRanked?: boolean;
}> = ({
  option,
  currentVote,
  userHasVoted,
  voteCounts,
  voterNames,
  onVoteChange,
  onHaptic,
  rank,
  showDate = true,
  isTopRanked = false,
}) => {
  const { t } = useTranslation("common");
  const [isDragging, setIsDragging] = useState(false);
  const [showVoters, setShowVoters] = useState(false);

  // Format de la date (éviter les décalages timezone)
  const formatDateLocal = (dateString: string) => {
    // Parser la date en mode local pour éviter les décalages timezone
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month - 1 car JS commence à 0

    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  // Format d'un créneau horaire unique (une ligne = un créneau)
  const formatTimeSlot = (timeSlot: {
    hour: number;
    minute: number;
    duration?: number;
  }): string => {
    if (
      !timeSlot ||
      typeof timeSlot.hour !== "number" ||
      typeof timeSlot.minute !== "number"
    ) {
      return t("vote_grid.all_day");
    }

    const startHour = timeSlot.hour.toString().padStart(2, "0");
    const startMinute = timeSlot.minute.toString().padStart(2, "0");
    const duration = timeSlot.duration || 30; // Durée par défaut de 30 minutes

    const endTime = timeSlot.hour * 60 + timeSlot.minute + duration;
    const endHour = Math.floor(endTime / 60);
    const endMinute = endTime % 60;
    const endHourStr = endHour.toString().padStart(2, "0");
    const endMinuteStr = endMinute.toString().padStart(2, "0");

    return `${startHour}:${startMinute} - ${endHourStr}:${endMinuteStr}`;
  };

  // Gestion des gestes de swipe
  const handlePan = (_event: unknown, info: PanInfo) => {
    const { offset } = info;
    const threshold = 50;

    if (Math.abs(offset.x) > threshold) {
      if (offset.x > 0) {
        // Swipe droite = Non
        onVoteChange("no");
      } else {
        // Swipe gauche = Oui
        onVoteChange("yes");
      }
      onHaptic("medium");
    }
  };

  const handlePanStart = () => {
    setIsDragging(true);
    onHaptic("light");
  };

  const handlePanEnd = () => {
    setIsDragging(false);
  };

  const totalVotes = voteCounts.yes + voteCounts.no + voteCounts.maybe;
  const hasExistingVotes = totalVotes > 0;

  // Extraire la première heure pour le scroll automatique
  const firstHour = option.time_slots?.[0]?.hour ?? 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-hour={firstHour}
      className={`
        bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative
        ${isDragging ? "shadow-lg scale-[1.02]" : ""}
        ${currentVote && userHasVoted ? "ring-2 ring-blue-200" : ""}
        ${rank === 1 && hasExistingVotes ? "border-2 border-blue-500" : ""}
      `}
    >
      {/* Badge pour options les plus populaires - toutes les options ex aequo */}
      {hasExistingVotes && isTopRanked && (
        <div className="absolute -top-2 -left-2 z-30 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
          {t("vote_grid.top_ranked_badge")}
        </div>
      )}

      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        onPanStart={handlePanStart}
        onPan={handlePan}
        onPanEnd={handlePanEnd}
        className="p-3 cursor-grab active:cursor-grabbing"
      >
        {/* Contenu sur une seule ligne */}
        <div className="flex items-center gap-3">
          {/* Icône calendrier - seulement si on affiche la date */}
          {showDate && (
            <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
          )}

          {/* Date et horaires */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {showDate && (
                <h3 className="font-semibold text-gray-900 capitalize text-sm">
                  {formatDateLocal(option.option_date)}
                </h3>
              )}
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <Clock className="h-3 w-3" />
                <span>
                  {option.time_slots.length > 0
                    ? formatTimeSlot(option.time_slots[0])
                    : t("vote_grid.all_day")}
                </span>
              </div>
            </div>
          </div>

          {/* Boutons de vote */}
          <div className="flex gap-2 flex-shrink-0">
            <VoteButton
              type="yes"
              isSelected={currentVote === "yes"}
              isColored={currentVote === "yes" && userHasVoted}
              onSelect={() => onVoteChange("yes")}
              onHaptic={onHaptic}
            />
            <VoteButton
              type="maybe"
              isSelected={currentVote === "maybe"}
              isColored={
                (currentVote === "maybe" && userHasVoted) ||
                (!userHasVoted && currentVote === "maybe")
              }
              onSelect={() => onVoteChange("maybe")}
              onHaptic={onHaptic}
            />
            <VoteButton
              type="no"
              isSelected={currentVote === "no"}
              isColored={currentVote === "no" && userHasVoted}
              onSelect={() => onVoteChange("no")}
              onHaptic={onHaptic}
            />
          </div>
        </div>

        {/* Système de barres de progression à double couche - seulement si des votes existent */}
        {hasExistingVotes && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex justify-between text-xs text-gray-600 mb-2">
              <span>
                {totalVotes}{" "}
                {totalVotes !== 1
                  ? t("vote_grid.votes_plural")
                  : t("vote_grid.votes_singular")}
              </span>
              <span className="flex gap-2">
                <span className="text-blue-600 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  {voteCounts.yes}
                </span>
                <span className="text-orange-600 flex items-center gap-1">
                  <HelpCircle className="h-3 w-3" />
                  {voteCounts.maybe}
                </span>
                <span className="text-red-600 flex items-center gap-1">
                  <X className="h-3 w-3" />
                  {voteCounts.no}
                </span>
              </span>
            </div>

            {/* Barres de progression pour chaque type de vote */}
            <div className="space-y-1">
              <ProgressBar
                type="yes"
                existingCount={voteCounts.yes}
                totalExisting={totalVotes}
                userVote={currentVote}
                userHasVoted={userHasVoted}
              />
              <ProgressBar
                type="maybe"
                existingCount={voteCounts.maybe}
                totalExisting={totalVotes}
                userVote={currentVote}
                userHasVoted={userHasVoted}
              />
              <ProgressBar
                type="no"
                existingCount={voteCounts.no}
                totalExisting={totalVotes}
                userVote={currentVote}
                userHasVoted={userHasVoted}
              />
            </div>

            {/* Section des votants - seulement si des gens ont voté "oui" */}
            {voterNames.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setShowVoters(!showVoters)}
                  className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Eye className="h-3 w-3" />
                  <span>
                    {showVoters ? t("vote_grid.hide") : t("vote_grid.show")}{" "}
                    {voterNames.length}{" "}
                    {voterNames.length !== 1
                      ? t("vote_grid.respondents_plural")
                      : t("vote_grid.respondents_singular")}
                  </span>
                </button>

                {showVoters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <div className="bg-blue-50 rounded-lg p-2">
                      <div className="flex flex-wrap gap-1">
                        {voterNames.map((name, index) => (
                          <span
                            key={index}
                            className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Indicateur de swipe */}
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-blue-500/10 flex items-center justify-center pointer-events-none"
        >
          <div className="bg-white/90 rounded-lg px-3 py-1 text-sm font-medium text-gray-700">
            {t("vote_grid.swipe_hint_inline")}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export const VoteGrid: React.FC<VoteGridProps> = ({
  options,
  votes,
  currentVote,
  userHasVoted,
  onVoteChange,
  onHaptic,
}) => {
  const { t } = useTranslation("common");
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  // Calculer les statistiques de vote par option
  const getVoteStats = (optionId: string) => {
    const counts = { yes: 0, no: 0, maybe: 0 };
    const voterNames: string[] = [];

    votes.forEach((vote) => {
      const selection = vote.response_data[optionId];
      if (selection && selection in counts) {
        counts[selection]++;
        if (selection === "yes" && vote.respondent_name) {
          // Formater le hash pour l'affichage (plus lisible)
          const displayName = formatHashDisplay(vote.respondent_name);
          voterNames.push(displayName);
        }
      }
    });

    return { counts, voterNames };
  };

  // Fonction locale pour formater les hashes (fallback)
  const formatHashDisplay = (hash: string): string => {
    if (hash.length <= 8) return hash;
    return `${hash.substring(0, 4)}...${hash.substring(hash.length - 4)}`;
  };

  // Calculer les rangs basés sur le nombre de votes "oui" - recalculé à chaque render
  const optionsWithScores = React.useMemo(() => {
    return options.map((option) => ({
      ...option,
      score: getVoteStats(option.id).counts.yes,
    }));
  }, [options, votes]); // Dépend de votes pour être dynamique

  // Trier par score décroissant
  const sortedOptions = React.useMemo(() => {
    return [...optionsWithScores].sort((a, b) => b.score - a.score);
  }, [optionsWithScores]);

  // Créer un map des rangs - recalculé dynamiquement
  const rankMap = React.useMemo(() => {
    const map = new Map<string, number>();
    sortedOptions.forEach((option, index) => {
      // Si même score que le précédent, même rang
      if (index > 0 && option.score === sortedOptions[index - 1].score) {
        map.set(option.id, map.get(sortedOptions[index - 1].id)!);
      } else {
        map.set(option.id, index + 1);
      }
    });
    return map;
  }, [sortedOptions]);

  if (options.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">{t("vote_grid.no_options")}</p>
      </div>
    );
  }

  // Grouper les options par date
  const optionsByDate = new Map<string, PollOption[]>();
  options.forEach((option) => {
    const date = option.option_date;
    if (!optionsByDate.has(date)) {
      optionsByDate.set(date, []);
    }
    optionsByDate.get(date)!.push(option);
  });

  // Trier les dates
  const sortedDates = Array.from(optionsByDate.keys()).sort();

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Formater une date pour l'affichage
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("vote_grid.available_options")}
        </h2>
        <div className="text-sm text-gray-600">
          {typeof window !== "undefined" && window.innerWidth >= 768
            ? t("vote_grid.click_to_vote")
            : t("vote_grid.swipe_to_vote")}
        </div>
      </div>

      <div className="space-y-2">
        {sortedDates.map((date, dateIndex) => {
          const dateOptions = optionsByDate.get(date)!;
          const isExpanded = expandedDates.has(date);
          const hasMultipleSlots = dateOptions.length > 1;

          return (
            <motion.div
              key={date}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: dateIndex * 0.05 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* En-tête de date avec bouton d'expansion */}
              {hasMultipleSlots && (
                <button
                  onClick={() => toggleDate(date)}
                  className="w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                  data-testid="votegrid-button"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900 capitalize text-sm">
                      {formatDate(date)}
                    </h3>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {dateOptions.length}{" "}
                      {dateOptions.length > 1
                        ? t("vote_grid.time_slots_plural")
                        : t("vote_grid.time_slots_singular")}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              )}

              {/* Liste des créneaux */}
              <div className={hasMultipleSlots && !isExpanded ? "hidden" : ""}>
                {dateOptions.map((option, slotIndex) => {
                  const voteStats = getVoteStats(option.id);
                  return (
                    <div
                      key={option.id}
                      className={
                        hasMultipleSlots ? "border-t border-gray-100" : ""
                      }
                    >
                      <OptionCard
                        option={option}
                        currentVote={currentVote[option.id]}
                        userHasVoted={userHasVoted[option.id] || false}
                        voteCounts={voteStats.counts}
                        voterNames={voteStats.voterNames}
                        onVoteChange={(value) => onVoteChange(option.id, value)}
                        onHaptic={onHaptic}
                        rank={rankMap.get(option.id)}
                        showDate={!hasMultipleSlots}
                        isTopRanked={rankMap.get(option.id) === 1}
                      />
                    </div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Aide pour les gestes */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-blue-50 rounded-xl p-3 text-center"
      >
        <p className="text-xs text-blue-600">
          💡 <span className="font-medium">{t("vote_grid.tip_label")}</span>{" "}
          {t("vote_grid.swipe_tip")}
        </p>
      </motion.div>
    </motion.div>
  );
};
